<template>
  <div class="bg-gray-50 p-4 rounded-lg space-y-4">
    <!-- Text Input -->
    <div>
      <label for="middleText" class="block text-sm font-medium text-gray-700 mb-1">
        Text Content
      </label>
      <textarea
        id="middleText"
        v-model="middleSectionText"
        :disabled="!isOnline"
        placeholder="Max 18 chars per line, 4 lines"
        rows="4"
        maxlength="80"
        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        @input="handleInputLimit"
      />
      <p class="text-xs text-gray-500 mt-1">
        Max 18 characters per line, 4 lines.
      </p>
    </div>
    <!-- Color Selection -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-2">
        Text Color
      </label>
      <div class="flex space-x-3">
        <label class="flex items-center">
          <input
            type="radio"
            value="black"
            v-model="middleSectionColor"
            :disabled="!isOnline"
            @change="updateMiddleSection"
            class="mr-2 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
          />
          <span class="text-sm text-gray-700">Black</span>
        </label>
        <label class="flex items-center">
          <input
            type="radio"
            value="red"
            v-model="middleSectionColor"
            :disabled="!isOnline"
            @change="updateMiddleSection"
            class="mr-2 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
          />
          <span class="text-sm text-gray-700">Red</span>
        </label>
      </div>
    </div>
    <!-- Current Status -->
    <div v-if="device.shadow?.state?.displayConfig?.middleSection" class="text-xs text-gray-500 bg-white p-2 rounded border">
      <div>Current: "{{ device.shadow.state.displayConfig.middleSection.text || '(empty)' }}"</div>
      <div>Color: {{ device.shadow.state.displayConfig.middleSection.color || 'red' }}</div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { debounce } from 'lodash-es'

const props = defineProps({
  device: {
    type: Object,
    required: true
  },
  isOnline: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['update-desired'])


const middleSectionText = ref('')
const middleSectionColor = ref('red')

// Helper to get the most relevant middleSection (desired > reported)
function getMiddleSection() {
  const desired = props.device.shadow?.desired?.displayConfig?.middleSection
  if (desired && (desired.text || desired.color)) return desired
  const reported = props.device.shadow?.state?.displayConfig?.middleSection
  if (reported && (reported.text || reported.color)) return reported
  return { text: '', color: 'red' }
}

// Watch both desired and reported for changes
watch(
  () => [
    props.device.shadow?.desired?.displayConfig?.middleSection,
    props.device.shadow?.state?.displayConfig?.middleSection
  ],
  () => {
    const section = getMiddleSection()
    middleSectionText.value = section.text || ''
    middleSectionColor.value = section.color || 'red'
  },
  { immediate: true, deep: true }
)

const sendMiddleSectionUpdate = () => {
  const newState = {
    displayConfig: {
      middleSection: {
        text: middleSectionText.value,
        color: middleSectionColor.value
      }
    }
  }
  emit('update-desired', props.device.deviceId, newState)
}

const updateMiddleSection = debounce(sendMiddleSectionUpdate, 2000)

// Enforce 18 chars per line, 4 lines max
const handleInputLimit = (e) => {
  let lines = e.target.value.split(/\r?\n/).slice(0, 4)
  lines = lines.map(line => line.length > 18 ? line.slice(0, 18) : line)
  const limited = lines.join('\n')
  if (limited !== middleSectionText.value) {
    middleSectionText.value = limited
  }
  updateMiddleSection()
}
</script>
